import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { apiGet, formatLKR, formatPct } from "@/lib/api";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import SignalBadge from "@/components/common/SignalBadge";
import ValuationBadge, { matchesValuationFilter, valuationSortRank } from "@/components/common/ValuationBadge";
import SignalRationaleTooltip from "@/components/stocks/SignalRationaleTooltip";
import { CompanyLink, SymbolLink } from "@/components/common/StockLinks";
import WatchlistStarButton from "@/components/common/WatchlistStarButton";
import HelpTooltip from "@/components/common/HelpTooltip";
import ForeignFlowChip from "@/components/common/ForeignFlowChip";
import DataConfidenceDot from "@/components/common/DataConfidenceDot";
import DeepAISignalBadge from "@/components/common/DeepAISignalBadge";
import AdminBulkActionBar from "@/components/admin/AdminBulkActionBar";
import TargetCellPopover from "@/components/stocks/TargetCellPopover";
import { abbreviateSector, verdictPillClasses, verdictShort } from "@/lib/sectorDisplay";
import { useForeignFlowChips } from "@/lib/marketIntelligence";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Search, ArrowUpRight, ArrowDownRight, AlertTriangle, Building2, GitCompare, X, FileText, Sparkles, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const SHARE_TYPE_OPTIONS = [
  { value: "all", label: "All share types" },
  { value: "voting", label: "Voting only" },
  { value: "non_voting", label: "Non-voting only" },
];

const SIGNAL_OPTIONS = [
  { value: "all", label: "All signals" },
  // STRONG_BUY uses the underscored form to match the canonical composite_signal
  // value emitted by scoring_engine (see scoring_engine.py:_signal_from_score).
  // Listed first so the most bullish verdict is closest to the "All" default.
  { value: "STRONG_BUY", label: "STRONG BUY" },
  { value: "BUY", label: "BUY" },
  { value: "WATCH", label: "WATCH" },
  { value: "HOLD", label: "HOLD" },
  { value: "AVOID", label: "AVOID" },
  { value: "NO_DATA", label: "No data" },
];

const SORT_OPTIONS = [
  { value: "score_desc", label: "Score (high → low)" },
  { value: "company_name_asc", label: "Company (A–Z)" },
  { value: "dividend_yield_desc", label: "Dividend yield (high → low)" },
  { value: "pe_ratio_asc", label: "P/E (low → high)" },
  { value: "change_pct_desc", label: "Δ today (high → low)" },
  { value: "target_upside_pct_desc", label: "Target upside (high → low)" },
  { value: "market_cap_desc", label: "Market cap (high → low)" },
  { value: "deep_signal_rank_desc", label: "Deep AI signal (best first)" },
];

// Ranking used for sorting the Deep AI column. Higher value = stronger.
const DEEP_SIGNAL_RANK = {
  STRONG_BUY: 5,
  BUY: 4,
  WATCH: 3,
  HOLD: 2,
  AVOID: 1,
};

const DEEP_AI_FILTER_OPTIONS = [
  { value: "all", label: "All Deep AI signals" },
  { value: "STRONG_BUY", label: "Deep AI: STRONG BUY" },
  { value: "BUY", label: "Deep AI: BUY" },
  { value: "WATCH", label: "Deep AI: WATCH" },
  { value: "HOLD", label: "Deep AI: HOLD" },
  { value: "AVOID", label: "Deep AI: AVOID" },
  { value: "HAS_ANALYSIS", label: "Has deep analysis" },
  { value: "NO_ANALYSIS", label: "No deep analysis yet" },
];

const StocksPage = () => {
  const [overview, setOverview] = useState(null);
  const [aiList, setAiList] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [sectorScores, setSectorScores] = useState({}); // sector name -> L6 row
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSector = searchParams.get("sector") || "all";
  // Issue #1 — honour ?signal=BUY / ?sort=change_desc|yield_desc coming
  // from clickable Dashboard KPI cards.
  const _initialSignalParam = (searchParams.get("signal") || "").toUpperCase();
  const initialSignal = ["ALL", "BUY", "WATCH", "HOLD", "AVOID", "NO_DATA"].includes(_initialSignalParam)
    ? (_initialSignalParam === "ALL" ? "all" : _initialSignalParam)
    : "all";
  const _sortParam = (searchParams.get("sort") || "").toLowerCase();
  const _SORT_ALIASES = {
    change_desc: "change_pct_desc",
    yield_desc: "dividend_yield_desc",
    score_desc: "score_desc",
    mcap_desc: "market_cap_desc",
    company_asc: "company_name_asc",
    pe_asc: "pe_ratio_asc",
  };
  const initialSort = _SORT_ALIASES[_sortParam] || (_sortParam || "score_desc");
  // Phase 12.7 (2026-05-15) — initial `page` from URL so that pressing
  // browser-back from a stock detail page lands the user on the SAME
  // page of the screener they were viewing. Without this, navigating
  // from page 5 → detail → back would land on page 1.
  const _initialPageParam = parseInt(searchParams.get("page") || "1", 10);
  const _initialPage = Number.isFinite(_initialPageParam) && _initialPageParam > 0
    ? _initialPageParam
    : 1;
  // Phase 12.7 — also restore the search query from `?q=` so shared
  // URLs reproduce the exact filtered view.
  const _initialSearch = searchParams.get("q") || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(_initialSearch);
  const [sector, setSector] = useState(initialSector);
  const [shareType, setShareType] = useState("all");
  const [signal, setSignal] = useState(initialSignal);
  const [sort, setSort] = useState(initialSort);
  const [showNoData, setShowNoData] = useState(false);
  // Improvement #1 — Data Quality filter
  const [dataQuality, setDataQuality] = useState("all"); // all | high | needs_reports
  // Layer 6 — Sector verdict filter
  const _initialL6 = (searchParams.get("sector_verdict") || "all").toUpperCase();
  const [layer6Filter, setLayer6Filter] = useState(
    ["ALL", "OVERWEIGHT", "NEUTRAL", "UNDERWEIGHT"].includes(_initialL6)
      ? (_initialL6 === "ALL" ? "all" : _initialL6)
      : "all"
  );
  const [page, setPage] = useState(_initialPage);
  const [selected, setSelected] = useState(() => new Set());
  // Phase 5+ — Deep AI signal filter (screener column)
  const [deepAiFilter, setDeepAiFilter] = useState("all");
  // Valuation Status filter (2026-05-03) — All / Undervalued / Fair / Overvalued
  const _initialValuation = (searchParams.get("valuation") || "all").toLowerCase();
  const [valuationFilter, setValuationFilter] = useState(
    ["all", "undervalued", "fairly_valued", "overvalued"].includes(_initialValuation)
      ? _initialValuation
      : "all"
  );
  // Phase 8 — IPO filter (lets users narrow the screener to only
  // stocks that have an analysed IPO record in `ipo_analyses`).
  const _initialIpo = (searchParams.get("ipo") || "all").toLowerCase();
  const [ipoFilter, setIpoFilter] = useState(
    ["all", "only"].includes(_initialIpo) ? _initialIpo : "all"
  );
  const [ipoSymbols, setIpoSymbols] = useState(() => new Set());

  useEffect(() => {
    // Fire-and-forget — if the IPO endpoint 404s or errors the badge just
    // won't render; it should never block the screener from loading.
    import("@/lib/api").then(({ apiGet }) => {
      apiGet("/ipo/list")
        .then((buckets) => {
          const all = new Set();
          ["upcoming", "open", "recently_listed", "historical"].forEach((k) => {
            (buckets?.[k] || []).forEach((r) => r?.symbol && all.add(r.symbol));
          });
          setIpoSymbols(all);
        })
        .catch(() => setIpoSymbols(new Set()));
    });
  }, []);
  const { isAdmin, isPremium, user } = useAuth();
  const showDeepAiSignals = isAdmin || isPremium;
  const usStocksVisible = !!(isAdmin || (user && user.us_stocks_enabled));
  const foreignChips = useForeignFlowChips();

  // Admin-only — bulk report selection (3-button actions). Distinct
  // from `selected` which is the up-to-3 Compare-picker. The screener
  // checkboxes stay hidden for non-admins.
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const toggleBulkSelected = (sym) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sym)) next.delete(sym);
      else next.add(sym);
      return next;
    });
  };
  const clearBulkSelected = () => setBulkSelected(new Set());

  const toggleSelected = (sym) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sym)) next.delete(sym);
      else if (next.size < 3) next.add(sym);
      return next;
    });
  };
  const clearSelected = () => setSelected(new Set());
  const PAGE_SIZE = 25;
  const navigate = useNavigate();
  const compareSelected = () => {
    if (selected.size === 0) return;
    navigate(`/compare?stocks=${Array.from(selected).join(",")}`);
  };

  // Track "recent IPOs" (upcoming/open/closed or listed <12 months) so
  // the screener can render an IPO badge instead of the 6-layer AVOID
  // signal. A pre-listing stock has no BVPS/ROE so the composite score
  // plunges to ~20 and the `AVOID` pill is wildly misleading vs the
  // qualitative IPO recommendation. `ipoMeta` is a dict keyed by symbol
  // storing status + recommendation signal so each cell can render the
  // correct IPO badge. (There's a separate `ipoSymbols` Set elsewhere
  // used only for filter toggles.)
  const [ipoMeta, setIpoMeta] = useState({});
  useEffect(() => {
    apiGet("/ipo/list").then((buckets) => {
      const map = {};
      const recentBuckets = ["upcoming", "open", "recently_listed", "pending_prospectus"];
      for (const key of recentBuckets) {
        for (const r of (buckets?.[key] || [])) {
          map[r.symbol] = {
            status: r.ipo_details?.ipo_status || key,
            signal: r.ipo_recommendation?.signal || null,
            conviction: r.ipo_recommendation?.conviction || null,
          };
        }
      }
      for (const r of (buckets?.historical || [])) {
        const listDate = r.ipo_details?.listing_date;
        if (!listDate) continue;
        const ageDays = (Date.now() - new Date(listDate).getTime()) / (1000 * 60 * 60 * 24);
        if (ageDays < 365) {
          map[r.symbol] = {
            status: "listed",
            signal: r.ipo_recommendation?.signal || null,
            conviction: r.ipo_recommendation?.conviction || null,
          };
        }
      }
      setIpoMeta(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      apiGet("/market/overview"),
      apiGet("/sectors"),
      apiGet("/ai-analysis"),
      apiGet("/sectors/scores").catch(() => ({ items: [] })),
    ])
      .then(([o, s, ai, scoreResp]) => {
        setOverview(o);
        setSectors(s);
        setAiList(ai);
        const map = {};
        (scoreResp?.items || []).forEach((row) => {
          if (row.sector) map[row.sector] = row;
        });
        setSectorScores(map);
      })
      .catch((e) => setError(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  // Build a quick lookup: cse_symbol -> { score, signal }
  const aiBySymbol = useMemo(() => {
    const map = {};
    for (const a of aiList) {
      for (const sa of a.securities_analysis || []) {
        map[sa.cse_symbol] = { score: sa.score, signal: sa.signal };
      }
    }
    return map;
  }, [aiList]);

  const rows = useMemo(() => {
    if (!overview) return [];
    let out = overview.securities.map((r) => ({
      ...r,
      // Prefer real-time composite score over the older AI-list lookup.
      score: r.composite_score ?? aiBySymbol[r.cse_symbol]?.score ?? null,
      signal: r.composite_signal ?? aiBySymbol[r.cse_symbol]?.signal ?? null,
      // Numeric rank for sorting the Deep AI column. Null if no analysis.
      deep_signal_rank:
        r.deep_coordinator_signal != null
          ? DEEP_SIGNAL_RANK[String(r.deep_coordinator_signal).toUpperCase()] ?? 0
          : null,
    }));

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(
        (r) =>
          r.company_name.toLowerCase().includes(q) ||
          r.cse_symbol.toLowerCase().includes(q) ||
          r.company_code.toLowerCase().includes(q),
      );
    }
    if (sector !== "all") out = out.filter((r) => r.sector === sector);
    if (shareType !== "all") out = out.filter((r) => r.share_type === shareType);
    if (signal !== "all") {
      out = out.filter((r) => (r.signal || "NO_DATA") === signal);
    }
    if (!showNoData) {
      out = out.filter((r) => (r.signal || "NO_DATA") !== "NO_DATA" || r.pe_ratio != null || r.dividend_yield != null);
    }
    // Improvement #1 — Data Quality filter
    if (dataQuality === "high") {
      out = out.filter((r) => r.data_confidence === "high");
    } else if (dataQuality === "needs_reports") {
      out = out.filter((r) => r.data_confidence === "low" || r.data_confidence === "minimal");
    }
    // Layer 6 — Sector verdict filter
    if (layer6Filter !== "all") {
      out = out.filter((r) => {
        const v = (sectorScores[r.sector]?.overall_verdict || "").toUpperCase();
        return v === layer6Filter;
      });
    }

    // Deep AI signal filter (Premium / Admin only — non-premium users
    // see a locked column and the filter is hidden, but we still apply
    // the filter defensively in case a saved preset arrives).
    if (deepAiFilter !== "all") {
      out = out.filter((r) => {
        const sig = r.deep_coordinator_signal
          ? String(r.deep_coordinator_signal).toUpperCase().replace(/[\s-]/g, "_")
          : null;
        if (deepAiFilter === "HAS_ANALYSIS") return !!sig;
        if (deepAiFilter === "NO_ANALYSIS") return !sig;
        return sig === deepAiFilter;
      });
    }

    // IPO filter — narrow to rows matching an entry in `ipo_analyses`.
    if (ipoFilter === "only" && ipoSymbols.size > 0) {
      out = out.filter((r) => ipoSymbols.has(r.cse_symbol));
    }

    // Valuation Status filter (2026-05-03)
    if (valuationFilter && valuationFilter !== "all") {
      out = out.filter((r) => matchesValuationFilter(r.valuation_status, valuationFilter));
    }

    const [key, dir] = sort.split("_").reduce((acc, part, idx, arr) => {
      // last token is direction, everything before is the key
      if (idx === arr.length - 1) return [acc[0], part];
      return [acc[0] === "" ? part : `${acc[0]}_${part}`, acc[1]];
    }, ["", "asc"]);

    out = out.slice().sort((a, b) => {
      // Custom sort: `valuation_status` is an enum — sort by rank, not
      // alphabetically. Most undervalued first when dir=desc.
      if (key === "valuation_status") {
        const ra = valuationSortRank(a.valuation_status);
        const rb = valuationSortRank(b.valuation_status);
        if (ra === rb) {
          // tie-break by discount_pct (more positive = more undervalued)
          const da = a.valuation_discount_pct ?? -9999;
          const db = b.valuation_discount_pct ?? -9999;
          return dir === "asc" ? da - db : db - da;
        }
        return dir === "asc" ? ra - rb : rb - ra;
      }
      const av = a[key];
      const bv = b[key];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "string") return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return dir === "asc" ? av - bv : bv - av;
    });
    return out;
  }, [overview, aiBySymbol, search, sector, shareType, signal, sort, showNoData, dataQuality, layer6Filter, sectorScores, deepAiFilter, ipoFilter, ipoSymbols, valuationFilter]);

  // Reset to page 1 whenever filters/search change. (Page changes from
  // the pagination component itself are excluded — they reset nothing.)
  //
  // Phase 12.7 — StrictMode-safe guard. Earlier `_firstResetRef` was
  // broken in dev because StrictMode double-fires effects: the FIRST
  // run flipped the ref to false, then the SECOND run saw `false` and
  // executed `setPage(1)`, clobbering the page restored from the URL.
  // The signature pattern below compares current filters against the
  // last-seen filters; equal → no-op (covers both StrictMode runs +
  // unrelated re-renders), differ → reset to page 1.
  //
  // CRITICAL: the initial signature MUST mirror the actual useState
  // defaults above. An off-by-one default (e.g. shareType "" vs "all")
  // makes the very first useEffect run think filters have changed and
  // resets the page state — silently breaking the URL-restored ?page=5.
  const _lastFilterSigRef = useRef(
    JSON.stringify([
      _initialSearch,    // search
      initialSector,     // sector
      "all",             // shareType (matches useState default)
      initialSignal,     // signal
      initialSort,       // sort
      false,             // showNoData (matches useState default)
      "all",             // dataQuality (matches useState default)
      "all",             // layer6Filter (matches useState default)
      "all",             // deepAiFilter (matches useState default)
      "all",             // ipoFilter (matches useState default)
      "all",             // valuationFilter (matches useState default)
    ]),
  );
  useEffect(() => {
    const sig = JSON.stringify([
      search, sector, shareType, signal, sort,
      showNoData, dataQuality, layer6Filter, deepAiFilter, ipoFilter, valuationFilter,
    ]);
    if (sig !== _lastFilterSigRef.current) {
      _lastFilterSigRef.current = sig;
      setPage(1);
    }
  }, [search, sector, shareType, signal, sort, showNoData, dataQuality, layer6Filter, deepAiFilter, ipoFilter, valuationFilter]);

  // Phase 12.7 (2026-05-15) — Sync ALL screener state to the URL. Each
  // user-driven change creates a real history entry (no `replace`) so
  // browser-back from a stock detail returns the user to the EXACT
  // page + filters they were viewing. Earlier experiments with
  // `replace: true` were unreliable in react-router-dom v6/v7 — the
  // replaced URLs occasionally did not persist into the history stack,
  // sending the back button to the original `/stocks` instead of
  // `/stocks?page=5`. Push semantics fix this at the cost of a few
  // extra back presses to escape the screener, which is acceptable.
  //
  // We omit values that match the default ("all"/empty/page=1) to keep
  // the URL clean — `/stocks` rather than `/stocks?page=1&sector=all`.
  // To avoid clobbering the URL on initial mount (esp. in React
  // StrictMode dev where effects double-fire), we only write to the URL
  // when the actual computed querystring DIFFERS from what's already
  // in `searchParams`. This is both the first-mount guard AND the
  // "no-op on repeat invocations" guard.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const setOrDelete = (key, val, isDefault) => {
      if (isDefault) next.delete(key);
      else next.set(key, String(val));
    };
    setOrDelete("page", page, page <= 1);
    setOrDelete("q", search, !search.trim());
    setOrDelete("sector", sector, sector === "all");
    setOrDelete("signal", signal, signal === "all");
    setOrDelete("sort", sort, sort === "score_desc");
    setOrDelete("valuation", valuationFilter, valuationFilter === "all");
    setOrDelete("ipo", ipoFilter, ipoFilter === "all");
    setOrDelete("sector_verdict", layer6Filter, layer6Filter === "all");
    // Only call setSearchParams when something actually changed —
    // avoids unnecessary history entries / re-render storms / and
    // (critically) prevents StrictMode's second effect run from
    // pushing a redundant identical URL.
    const currentStr = searchParams.toString();
    const nextStr = next.toString();
    if (currentStr !== nextStr) {
      setSearchParams(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, sector, signal, sort, valuationFilter, ipoFilter, layer6Filter]);

  // React to URL-driven sector changes (e.g. user clicks another sector card).
  useEffect(() => {
    const fromUrl = searchParams.get("sector") || "all";
    if (fromUrl !== sector) setSector(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [rows, safePage],
  );

  // Admin-only — select-all on the current page. Filters out symbols
  // already selected to toggle between "add all" and "remove all"
  // behaviours so clicking twice is an intuitive toggle.
  const pageSymbols = useMemo(() => pageRows.map((r) => r.cse_symbol), [pageRows]);
  const allPageSelected =
    pageSymbols.length > 0 && pageSymbols.every((s) => bulkSelected.has(s));
  const togglePageSelectAll = () => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageSymbols.forEach((s) => next.delete(s));
      } else {
        pageSymbols.forEach((s) => next.add(s));
      }
      return next;
    });
  };

  // Derive company_codes + display labels for the bulk action bar.
  // The bulk-action endpoint works on company_codes (e.g. "COMB")
  // rather than full security symbols ("COMB.N0000"), so we dedup
  // across voting / non-voting securities that share a parent company.
  const bulkSelectionMeta = useMemo(() => {
    const seen = new Set();
    const companyCodes = [];
    const labels = [];
    for (const sym of bulkSelected) {
      const row = rows.find((r) => r.cse_symbol === sym);
      const code = row?.company_code || sym.split(".")[0];
      if (!code || seen.has(code)) continue;
      seen.add(code);
      companyCodes.push(code);
      labels.push(code);
    }
    return { companyCodes, labels };
  }, [bulkSelected, rows]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState description={error} />;

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-5" data-testid="stocks-page">
      {/* Market switcher — visible always for CSE stocks page; the
          US Stocks tab is gated on `user.us_stocks_enabled` (admins
          always see it). Persists choice in localStorage. */}
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex items-center rounded-lg border bg-muted/40 p-1" data-testid="stocks-market-switcher">
          <Button
            size="sm"
            variant="default"
            className="px-3 py-1 h-8"
            onClick={() => { try { localStorage.setItem("cse_market_pref", "CSE"); } catch { /* ignore */ } }}
            data-testid="stocks-switch-to-cse-active"
          >
            <span className="mr-1.5">🇱🇰</span> CSE Stocks
          </Button>
          {usStocksVisible && (
            <Button
              size="sm"
              variant="ghost"
              className="px-3 py-1 h-8"
              onClick={() => {
                try { localStorage.setItem("cse_market_pref", "US"); } catch { /* ignore */ }
                navigate("/us-stocks");
              }}
              data-testid="stocks-switch-to-us"
            >
              <span className="mr-1.5">🇺🇸</span> US Stocks
            </Button>
          )}
        </div>
      </div>

      {/* Market Valuation Overview — at-a-glance distribution chips
          driven by overview.kpis.valuation_buckets. Click to filter. */}
      {overview?.kpis?.valuation_total_with_data > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2.5 text-xs"
          data-testid="market-valuation-overview"
        >
          <span className="text-muted-foreground">
            Of <strong className="text-foreground">{overview.kpis.valuation_total_with_data}</strong> CSE stocks:
          </span>
          <button
            type="button"
            onClick={() => setValuationFilter(valuationFilter === "undervalued" ? "all" : "undervalued")}
            className={`rounded-full border px-2.5 py-0.5 transition-colors ${
              valuationFilter === "undervalued"
                ? "bg-[hsl(142_55%_42%)] text-white border-[hsl(142_55%_42%)]"
                : "bg-[hsl(142_55%_42%)]/10 text-[hsl(142_72%_29%)] border-[hsl(142_55%_42%)]/40 hover:bg-[hsl(142_55%_42%)]/20"
            }`}
            data-testid="market-valuation-chip-undervalued"
          >
            <strong>{overview.kpis.valuation_undervalued_total}</strong> Undervalued{" "}
            <span className="opacity-70">
              ({Math.round((overview.kpis.valuation_undervalued_total / overview.kpis.valuation_total_with_data) * 100)}%)
            </span>
          </button>
          <button
            type="button"
            onClick={() => setValuationFilter(valuationFilter === "fairly_valued" ? "all" : "fairly_valued")}
            className={`rounded-full border px-2.5 py-0.5 transition-colors ${
              valuationFilter === "fairly_valued"
                ? "bg-muted text-foreground border-foreground/40"
                : "bg-muted/40 text-foreground border-border hover:bg-muted"
            }`}
            data-testid="market-valuation-chip-fair"
          >
            <strong>{overview.kpis.valuation_buckets?.fairly_valued ?? 0}</strong> Fairly Valued{" "}
            <span className="opacity-70">
              ({Math.round(((overview.kpis.valuation_buckets?.fairly_valued ?? 0) / overview.kpis.valuation_total_with_data) * 100)}%)
            </span>
          </button>
          <button
            type="button"
            onClick={() => setValuationFilter(valuationFilter === "overvalued" ? "all" : "overvalued")}
            className={`rounded-full border px-2.5 py-0.5 transition-colors ${
              valuationFilter === "overvalued"
                ? "bg-[hsl(38_92%_55%)] text-[hsl(20_50%_15%)] border-[hsl(38_92%_50%)]"
                : "bg-[hsl(38_92%_55%)]/15 text-[hsl(20_60%_30%)] dark:text-[hsl(38_92%_75%)] border-[hsl(38_92%_55%)]/40 hover:bg-[hsl(38_92%_55%)]/25"
            }`}
            data-testid="market-valuation-chip-overvalued"
          >
            <strong>{overview.kpis.valuation_overvalued_total}</strong> Overvalued{" "}
            <span className="opacity-70">
              ({Math.round((overview.kpis.valuation_overvalued_total / overview.kpis.valuation_total_with_data) * 100)}%)
            </span>
          </button>
          {valuationFilter !== "all" && (
            <button
              type="button"
              onClick={() => setValuationFilter("all")}
              className="text-[10px] text-muted-foreground underline ml-1"
              data-testid="market-valuation-clear"
            >
              Clear filter
            </button>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="stocks-filters">
        <div className="relative md:col-span-2 lg:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="stocks-search-input"
            placeholder="Search company, ticker or code"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={signal} onValueChange={setSignal}>
          <SelectTrigger data-testid="stocks-signal-select">
            <SelectValue placeholder="Signal" />
          </SelectTrigger>
          <SelectContent>
            {SIGNAL_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={shareType} onValueChange={setShareType}>
          <SelectTrigger data-testid="stocks-sharetype-select">
            <SelectValue placeholder="Share type" />
          </SelectTrigger>
          <SelectContent>
            {SHARE_TYPE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sector} onValueChange={setSector}>
          <SelectTrigger data-testid="stocks-sector-select">
            <SelectValue placeholder="Sector" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sectors</SelectItem>
            {sectors.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="stocks-sort-select" className="lg:col-span-1">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Improvement #1 — Data Quality filter */}
        <Select value={dataQuality} onValueChange={setDataQuality}>
          <SelectTrigger data-testid="stocks-data-quality-select" className="lg:col-span-1">
            <SelectValue placeholder="Data quality" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="stocks-data-quality-all">All data quality</SelectItem>
            <SelectItem value="high" data-testid="stocks-data-quality-high">
              <span className="inline-flex items-center gap-2">
                <span className="block h-2 w-2 rounded-full bg-[hsl(var(--success))]" />
                High confidence only
              </span>
            </SelectItem>
            <SelectItem value="needs_reports" data-testid="stocks-data-quality-needs-reports">
              <span className="inline-flex items-center gap-2">
                <span className="block h-2 w-2 rounded-full bg-orange-500" />
                Needs reports
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        {/* Layer 6 — Sector Verdict filter */}
        <Select value={layer6Filter} onValueChange={setLayer6Filter}>
          <SelectTrigger data-testid="stocks-layer6-select" className="lg:col-span-1">
            <SelectValue placeholder="Sector verdict" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="stocks-l6-all">All sector verdicts</SelectItem>
            <SelectItem value="OVERWEIGHT" data-testid="stocks-l6-over">
              <span className="inline-flex items-center gap-2">
                <span className="block h-2 w-2 rounded-full bg-[hsl(142_72%_29%)]" />
                Overweight sectors
              </span>
            </SelectItem>
            <SelectItem value="NEUTRAL" data-testid="stocks-l6-neut">
              <span className="inline-flex items-center gap-2">
                <span className="block h-2 w-2 rounded-full bg-[hsl(221_83%_47%)]" />
                Neutral sectors
              </span>
            </SelectItem>
            <SelectItem value="UNDERWEIGHT" data-testid="stocks-l6-under">
              <span className="inline-flex items-center gap-2">
                <span className="block h-2 w-2 rounded-full bg-[hsl(0_72%_45%)]" />
                Underweight sectors
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        {/* Phase 5+ — Deep AI signal filter (Premium gated). Hidden for
            Free / Basic users since they can't see signal values anyway. */}
        {showDeepAiSignals && (
          <Select value={deepAiFilter} onValueChange={setDeepAiFilter}>
            <SelectTrigger data-testid="stocks-deep-ai-select" className="lg:col-span-1">
              <SelectValue placeholder="Deep AI" />
            </SelectTrigger>
            <SelectContent>
              {DEEP_AI_FILTER_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`stocks-deep-ai-${o.value.toLowerCase()}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {/* Phase 8 — IPO filter (shows only stocks that have an IPO analysis). */}
        {ipoSymbols.size > 0 && (
          <Select value={ipoFilter} onValueChange={setIpoFilter}>
            <SelectTrigger className="w-[140px]" data-testid="filter-ipo">
              <SelectValue placeholder="IPO" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stocks</SelectItem>
              <SelectItem value="only" data-testid="filter-ipo-only">IPO stocks only</SelectItem>
            </SelectContent>
          </Select>
        )}
        {/* Valuation filter (2026-05-03) — Undervalued / Fair / Overvalued. */}
        <Select value={valuationFilter} onValueChange={setValuationFilter}>
          <SelectTrigger className="w-[160px]" data-testid="filter-valuation">
            <SelectValue placeholder="Valuation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All valuations</SelectItem>
            <SelectItem value="undervalued" data-testid="filter-valuation-undervalued">Undervalued</SelectItem>
            <SelectItem value="fairly_valued" data-testid="filter-valuation-fair">Fairly valued</SelectItem>
            <SelectItem value="overvalued" data-testid="filter-valuation-overvalued">Overvalued</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground" data-testid="stocks-showall-row">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showNoData}
            onChange={(e) => setShowNoData(e.target.checked)}
            className="rounded border-border"
            data-testid="stocks-show-nodata-toggle"
          />
          Show all including <span className="font-mono px-1 py-0.5 rounded bg-muted">NO_DATA</span> stocks
        </label>
        <span data-testid="stocks-result-count">
          Showing <span className="font-medium text-foreground">{pageRows.length}</span> of {rows.length} stocks
          {totalPages > 1 && <> · page {safePage} of {totalPages}</>}
        </span>
      </div>

      {/* Compare Selected toolbar (Phase 7 Stage 3) */}
      {selected.size > 0 && (
        <div
          className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 flex flex-wrap items-center gap-3"
          data-testid="compare-toolbar"
        >
          <span className="text-sm font-medium inline-flex items-center gap-2">
            <GitCompare className="h-4 w-4 text-primary" />
            {selected.size} selected
          </span>
          <div className="flex flex-wrap gap-1.5">
            {Array.from(selected).map((sym) => (
              <Badge
                key={sym}
                variant="outline"
                className="gap-1 pl-2 pr-1 text-[11px] bg-card"
              >
                <span className="font-mono">{sym}</span>
                <button
                  type="button"
                  onClick={() => toggleSelected(sym)}
                  className="rounded p-0.5 hover:bg-muted"
                  aria-label={`Remove ${sym} from compare`}
                  data-testid={`compare-toolbar-remove-${sym}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelected}
              data-testid="compare-toolbar-clear"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={compareSelected}
              className="gap-2"
              data-testid="compare-toolbar-go"
            >
              <GitCompare className="h-3.5 w-3.5" />
              Compare {selected.size > 1 ? `(${selected.size})` : ""}
            </Button>
          </div>
        </div>
      )}

      <Card className="border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stocks-table">
            <thead className="bg-card sticky top-0">
              <tr className="border-b">
                {isAdmin && (
                  <th
                    className="px-2 py-3 text-xs uppercase tracking-wide text-muted-foreground text-center w-8"
                    title="Admin — select to bulk download / analyse reports"
                  >
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={togglePageSelectAll}
                      aria-label="Select all on this page"
                      className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary cursor-pointer"
                      data-testid="bulk-select-all-page"
                    />
                  </th>
                )}
                <th className="px-2 py-3 text-xs uppercase tracking-wide text-muted-foreground text-center w-8" title="Select to compare">
                  <GitCompare className="h-3 w-3 inline-block text-muted-foreground/70" />
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left w-8"></th>
                <th className="px-2 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right w-10">#</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Company</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">
                  <span className="inline-flex items-center gap-1">Sector<HelpTooltip termId="layer-6-sector-context" size="xs" /></span>
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Ticker</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">
                  <span className="inline-flex items-center">AI Signal<HelpTooltip termId="buy-signal" size="xs" /></span>
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">
                  <span
                    className="inline-flex items-center justify-end gap-1"
                    title="Composite score (0-100). The dot indicates data confidence: green=3+ annual reports, yellow=1-2 reports, orange=TradingView only, red=minimal data."
                  >
                    Score · Data<HelpTooltip termId="five-layer-score" size="xs" />
                  </span>
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">
                  <span className="inline-flex items-center">TV Signal<HelpTooltip termId="technical-signal" size="xs" /></span>
                </th>
                {/* Phase 5+ — Deep AI coordinator signal column (Premium gated) */}
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 cursor-help select-none"
                        onClick={() => setSort(sort === "deep_signal_rank_desc" ? "deep_signal_rank_asc" : "deep_signal_rank_desc")}
                        data-testid="deep-ai-column-header"
                      >
                        Deep AI
                        <Sparkles className="h-3 w-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      Deep Analysis coordinator signal from 6-specialist AI analysis (value, quality, dividend, risk, geopolitical, Sri Lanka macro). Premium feature. Click to sort.
                    </TooltipContent>
                  </Tooltip>
                </th>
                {/* Valuation Status — price vs fair value (target_base_case
                    with Graham fallback). Click to sort most-undervalued first. */}
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 cursor-help select-none"
                        onClick={() => setSort(sort === "valuation_status_desc" ? "valuation_status_asc" : "valuation_status_desc")}
                        data-testid="valuation-column-header"
                      >
                        Valuation
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      Current price vs. our base-case fair value (Graham Number fallback). Sorted most-undervalued first when descending.
                    </TooltipContent>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">Last</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">Δ today</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">
                  <span className="inline-flex items-center justify-end">Yield<HelpTooltip termId="dividend-yield" size="xs" /></span>
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">
                  <span className="inline-flex items-center justify-end">P/E<HelpTooltip termId="pe-ratio" size="xs" /></span>
                </th>
                <th
                  className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right"
                  title="12-month price target. Click any row's target to see all methods (PE, PB, DDM, Graham)."
                >
                  Target
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">Mkt cap</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 16 : 15} className="px-4 py-12 text-center text-sm text-muted-foreground">
                    No matches. Adjust filters.
                  </td>
                </tr>
              )}
              {pageRows.map((r, idx) => (
                <tr
                  key={r.cse_symbol}
                  data-testid="stocks-table-row"
                  onClick={() =>
                    navigate(`/stocks/${r.company_code}`, {
                      state: { from: { pathname: "/stocks", label: "Screener" } },
                    })
                  }
                  className="border-b last:border-0 hover:bg-muted/40 cursor-pointer transition-colors duration-150"
                >
                  {isAdmin && (
                    <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={bulkSelected.has(r.cse_symbol)}
                        onChange={() => toggleBulkSelected(r.cse_symbol)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary cursor-pointer"
                        aria-label={`Select ${r.cse_symbol} for bulk report action`}
                        data-testid={`bulk-checkbox-${r.cse_symbol}`}
                      />
                    </td>
                  )}
                  <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(r.cse_symbol)}
                      onChange={() => toggleSelected(r.cse_symbol)}
                      disabled={!selected.has(r.cse_symbol) && selected.size >= 3}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label={`Select ${r.cse_symbol} for comparison`}
                      data-testid={`compare-checkbox-${r.cse_symbol}`}
                    />
                  </td>
                  <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                    <WatchlistStarButton
                      cseSymbol={r.cse_symbol}
                      companyName={r.company_name}
                    />
                  </td>
                  <td className="px-2 py-3 text-right text-xs text-muted-foreground tabular" data-testid={`stocks-row-num-${(safePage - 1) * PAGE_SIZE + idx + 1}`}>
                    {(safePage - 1) * PAGE_SIZE + idx + 1}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2 min-w-0">
                      {/* Mobile-only data confidence dot — keeps the screener informative even
                          when the table scrolls horizontally and the Score · Data column is off-screen. */}
                      <span className="lg:hidden shrink-0">
                        <DataConfidenceDot
                          level={r.data_confidence}
                          annualReports={r.annual_reports_analysed || 0}
                          interimReports={r.interim_reports_analysed || 0}
                          hasTradingViewFundamentals={r.has_tradingview_fundamentals}
                          lastUpdated={r.score_computed_at || r.computed_at}
                          companyCode={r.company_code}
                          testId={`screener-data-confidence-mobile-${r.cse_symbol}`}
                        />
                      </span>
                      <CompanyLink
                        companyCode={r.company_code}
                        name={r.company_name}
                        fromLabel="Screener"
                        className="font-medium block min-w-0"
                        testId={`screener-name-${r.cse_symbol}`}
                      />
                    </div>
                    {/* Phase 8 — IPO badge links to the IPO detail page for stocks
                        that have an analysis. Separate from the report badge. */}
                    {ipoSymbols.has(r.cse_symbol) && (
                      <Link
                        to={`/ipo/${r.cse_symbol}`}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-0.5 inline-flex items-center gap-1 rounded-md bg-primary/10 text-primary border border-primary/30 px-1.5 py-0.5 text-[10px] font-medium hover:bg-primary/20 ml-1"
                        data-testid={`ipo-badge-${r.cse_symbol}`}
                        title="Has IPO analysis — click to view"
                      >
                        <Rocket className="h-2.5 w-2.5" />
                        IPO
                      </Link>
                    )}
                    {/* Improvement #1 — Highlight BUY stocks with zero analysed reports */}
                    {r.signal === "BUY" && (r.annual_reports_analysed || 0) === 0 && (
                      <Link
                        to={`/stocks/${r.company_code}?tab=reports`}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-0.5 inline-flex items-center gap-1 rounded-md bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/30 px-1.5 py-0.5 text-[10px] font-medium hover:bg-orange-500/20"
                        data-testid={`needs-reports-${r.cse_symbol}`}
                        title="No annual reports analysed yet — upload to deepen the BUY thesis"
                      >
                        <FileText className="h-2.5 w-2.5" />
                        Upload reports for full analysis
                      </Link>
                    )}
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                      <span className="font-mono">{r.company_code}</span>
                      <Badge variant="secondary" className="capitalize text-[10px] px-1.5 py-0">
                        {(r.share_type || "").replace("_", "-")}
                      </Badge>
                      {r.has_non_voting && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          dual class
                        </Badge>
                      )}
                      {r.is_investment_trust && (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="text-[10px] gap-0.5 px-1.5 py-0 border-info/40 text-info"
                                data-testid={`flag-investment-trust-${r.cse_symbol}`}
                              >
                                <Building2 className="h-2.5 w-2.5" />
                                Investment trust
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
                              Investment-holding company. PE and Graham Number
                              are excluded from scoring (earnings come from
                              portfolio gains).
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <ForeignFlowChip entry={foreignChips[r.cse_symbol]} />
                      {r.special_dividend_detected && (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="text-[10px] gap-0.5 px-1.5 py-0 border-warning/40 text-warning"
                                data-testid={`flag-special-dividend-${r.cse_symbol}`}
                              >
                                <AlertTriangle className="h-2.5 w-2.5" />
                                Special dividend
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
                              High yield with unsustainable payout. Yield
                              capped at sector median for scoring.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {r.lumpy_earnings && (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="text-[10px] gap-0.5 px-1.5 py-0 border-warning/40 text-warning"
                                data-testid={`flag-lumpy-${r.cse_symbol}`}
                              >
                                <AlertTriangle className="h-2.5 w-2.5" />
                                Lumpy EPS
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
                              Latest EPS is more than 2× the historical median.
                              Layer 2 EPS-growth score has been capped using the
                              median growth rate to neutralise the spike.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </td>
                  <td
                    className="px-3 py-3 text-muted-foreground"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="flex items-center gap-1.5 min-w-0"
                      title={r.sector || ""}
                    >
                      <span
                        className="truncate text-foreground/90"
                        data-testid={`screener-sector-${r.cse_symbol}`}
                      >
                        {abbreviateSector(r.sector)}
                      </span>
                      {(() => {
                        const sc = sectorScores[r.sector];
                        if (!sc) return null;
                        const v = (sc.overall_verdict || "").toUpperCase();
                        const short = verdictShort(v);
                        if (!short) return null;
                        // Phase 11 polish — solid filled pill (no outline
                        // border) so the bold background colour reads at a
                        // glance. Direct <span> instead of <Badge variant="outline">
                        // since the latter would inject a transparent border.
                        return (
                          <span
                            className={`inline-flex items-center rounded-full text-[9px] px-1.5 py-0.5 shrink-0 font-bold tracking-wider shadow-sm ${verdictPillClasses(v)}`}
                            title={`${r.sector} — Sector Layer 6 ${v} · ${sc.total_score}/${sc.max_score || 10}${sc.is_stale ? " · STALE" : ""}`}
                            data-testid={`screener-l6-${r.cse_symbol}`}
                          >
                            {short}
                          </span>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <SymbolLink
                      companyCode={r.company_code}
                      symbol={r.cse_symbol}
                      fromLabel="Screener"
                      testId={`screener-symbol-${r.cse_symbol}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const ipoInfo = ipoMeta[r.cse_symbol];
                      if (ipoInfo) {
                        // Render an IPO badge linking to the IPO detail
                        // page. Intentionally replaces the composite
                        // AVOID signal because the composite is
                        // meaningless for a pre-listing / freshly
                        // listed stock (missing BVPS/ROE → low score).
                        return (
                          <Link
                            to={`/ipo/${r.cse_symbol}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 rounded-md border border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/10 px-2 py-0.5 text-[11px] font-semibold text-[hsl(var(--info))] hover:bg-[hsl(var(--info))]/20 transition-colors"
                            title={
                              ipoInfo.signal
                                ? `IPO · ${ipoInfo.signal}${ipoInfo.conviction ? " (" + ipoInfo.conviction + ")" : ""}`
                                : "Recent IPO — click for full analysis"
                            }
                            data-testid={`screener-ipo-badge-${r.cse_symbol}`}
                          >
                            ★ IPO
                            {ipoInfo.signal === "STRONG SUBSCRIBE" && " · SUB"}
                            {ipoInfo.signal === "SUBSCRIBE" && " · SUB"}
                            {ipoInfo.signal === "WATCHLIST" && " · WATCH"}
                            {ipoInfo.signal === "AVOID" && " · AVOID"}
                          </Link>
                        );
                      }
                      if (r.signal) {
                        return (
                          <SignalRationaleTooltip
                            cseSymbol={r.cse_symbol}
                            companyCode={r.company_code}
                            companyName={r.company_name}
                            signal={r.signal}
                            score={r.composite_score}
                          >
                            <SignalBadge signal={r.signal} size="sm" />
                          </SignalRationaleTooltip>
                        );
                      }
                      return <NoDataBadge />;
                    })()}
                  </td>
                  <td
                    className="px-4 py-3 text-right tabular"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="inline-flex items-center justify-end gap-2">
                      <span className="font-display font-semibold text-foreground/90 tabular-nums">
                        {ipoMeta[r.cse_symbol] ? (
                          <span className="text-[11px] text-muted-foreground italic" title="Composite score isn't meaningful for pre-listing or recently-listed IPOs — see the IPO analysis page for the recommendation.">n/a</span>
                        ) : r.score_reliable === false ? (
                          /* 2026-05-09 — Score unreliable due to suspected
                             corporate action with no safe TV alternative.
                             Render "N/A" with tooltip so the displayed
                             value doesn't contradict the WATCH signal. */
                          <span
                            className="text-[11px] text-amber-600 dark:text-amber-400 italic font-medium"
                            title={r.data_quality_warning || "Score unavailable — EPS data requires manual verification due to suspected corporate action."}
                            data-testid={`screener-score-na-${r.cse_symbol}`}
                          >
                            N/A
                          </span>
                        ) : (
                          r.score ?? "—"
                        )}
                      </span>
                      <DataConfidenceDot
                        level={r.data_confidence}
                        annualReports={r.annual_reports_analysed || 0}
                        interimReports={r.interim_reports_analysed || 0}
                        hasTradingViewFundamentals={r.has_tradingview_fundamentals}
                        lastUpdated={r.score_computed_at || r.computed_at}
                        companyCode={r.company_code}
                        testId={`screener-data-confidence-${r.cse_symbol}`}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3"><SignalBadge signal={r.tv_signal} size="sm" /></td>
                  {/* Phase 5+ — Deep AI signal cell. Premium-gated; admins
                      always see real verdicts, others see a 🔒 placeholder.
                      v13.1 — Expired analyses still render (muted style)
                      and dual-class non-voting shares inherit the verdict
                      from their voting sibling via the backend join. */}
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <DeepAISignalBadge
                      signal={r.deep_coordinator_signal}
                      isPremium={showDeepAiSignals}
                      generatedAt={r.deep_generated_at}
                      isExpired={r.deep_is_expired || false}
                      validUntil={r.deep_valid_until}
                    />
                  </td>
                  {/* Valuation Status — short label pill */}
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <ValuationBadge
                      status={r.valuation_status}
                      discountPct={r.valuation_discount_pct}
                      size="sm"
                      short
                    />
                  </td>
                  <td className="px-4 py-3 text-right tabular">{formatLKR(r.close_price)}</td>
                  <td className="px-4 py-3 text-right tabular">
                    <ChangePill value={r.change_pct} />
                  </td>
                  <td className="px-4 py-3 text-right tabular">
                    {r.dividend_yield != null && r.dividend_yield > 0
                      ? formatPct(r.dividend_yield)
                      : <NoFundamentalCell />}
                  </td>
                  <td className="px-4 py-3 text-right tabular">
                    {r.pe_ratio != null && r.pe_ratio > 0
                      ? r.pe_ratio.toFixed(2)
                      : <NoFundamentalCell />}
                  </td>
                  <td
                    className="px-4 py-3 text-right tabular"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TargetCellPopover
                      cseSymbol={r.cse_symbol}
                      companyName={r.company_name}
                      currentPrice={r.close_price}
                      targetPe={r.target_pe_based}
                      targetPb={r.target_pb_based}
                      targetDdm={r.target_ddm}
                      targetGraham={r.target_graham}
                      targetAverage={r.target_average}
                      targetUpsidePct={r.target_upside_pct}
                      targetBaseCase={r.target_base_case}
                      targetBaseCaseUpsidePct={r.target_base_case_upside_pct}
                      testId={`screener-target-cell-${r.cse_symbol}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-right tabular">
                    {formatLKR(r.market_cap, { compact: true, decimals: 1 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {totalPages > 1 && (
        <div
          className="flex items-center justify-between gap-3 flex-wrap text-xs"
          data-testid="stocks-pagination"
        >
          <span className="text-muted-foreground">
            Showing rows <span className="text-foreground font-medium tabular">{(safePage - 1) * PAGE_SIZE + 1}</span>
            <span className="mx-1">–</span>
            <span className="text-foreground font-medium tabular">{Math.min(safePage * PAGE_SIZE, rows.length)}</span>
            <span className="mx-1">of</span>
            <span className="text-foreground font-medium tabular">{rows.length}</span>
          </span>
          <PaginationControls
            page={safePage}
            totalPages={totalPages}
            onChange={setPage}
          />
        </div>
      )}
      {/* Phase 10 (Fix 4) — Bottom spacer so the floating "Share feedback"
          button (fixed bottom-left) never sits over pagination controls. */}
      <div aria-hidden="true" className="h-20" />

      {/* Admin-only — Bulk report action bar. Appears sticky at the
          bottom of the viewport when 1+ admin checkbox is ticked on
          the screener. Hidden entirely for non-admin users. */}
      {isAdmin && (
        <AdminBulkActionBar
          companyCodes={bulkSelectionMeta.companyCodes}
          displayLabels={bulkSelectionMeta.labels}
          onClear={clearBulkSelected}
          reportTypes={["annual"]}
          yearsToKeep={5}
          limitPerCompany={10}
          analysisLimit={60}
          testIdPrefix="screener-bulk-bar"
        />
      )}
    </div>
    </TooltipProvider>
  );
};

const PaginationControls = ({ page, totalPages, onChange }) => {
  const go = (p) => onChange(Math.max(1, Math.min(totalPages, p)));
  // Compact paginator: first, prev, page n / m, next, last
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        className="h-8 px-2.5 rounded-md border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
        onClick={() => go(1)}
        disabled={page <= 1}
        data-testid="pagination-first"
      >
        «
      </button>
      <button
        type="button"
        className="h-8 px-2.5 rounded-md border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        data-testid="pagination-prev"
      >
        ‹ Prev
      </button>
      <span className="px-3 h-8 inline-flex items-center text-foreground tabular">
        {page} / {totalPages}
      </span>
      <button
        type="button"
        className="h-8 px-2.5 rounded-md border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
        onClick={() => go(page + 1)}
        disabled={page >= totalPages}
        data-testid="pagination-next"
      >
        Next ›
      </button>
      <button
        type="button"
        className="h-8 px-2.5 rounded-md border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
        onClick={() => go(totalPages)}
        disabled={page >= totalPages}
        data-testid="pagination-last"
      >
        »
      </button>
    </div>
  );
};

const ChangePill = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  const up = value >= 0;
  const Arrow = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 ${up ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}>
      <Arrow className="h-3 w-3" />
      {formatPct(value)}
    </span>
  );
};

const NoDataBadge = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Badge
        variant="outline"
        className="text-[10px] bg-muted text-muted-foreground border-border cursor-help"
        data-testid="signal-no-data"
      >
        No Data
      </Badge>
    </TooltipTrigger>
    <TooltipContent side="top">
      AI analysis not yet run for this stock. Visit the stock page to generate analysis.
    </TooltipContent>
  </Tooltip>
);

const NoFundamentalCell = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="text-muted-foreground cursor-help">—</span>
    </TooltipTrigger>
    <TooltipContent side="top">
      Fundamental data not yet available for this stock.
    </TooltipContent>
  </Tooltip>
);

/**
 * TradingView technical signal badge for the screener.
 *
 * Deprecated in favour of the shared `SignalBadge` component — kept as
 * a re-export so any external imports keep working but the rendering
 * now matches AI Signal + Deep AI Signal exactly (same pill shape,
 * same colour tokens). TV-specific labels (NEUTRAL / SELL / STRONG SELL)
 * are mapped onto the same colour family as the closest AI equivalent:
 *   NEUTRAL → HOLD colour (grey)
 *   SELL → WATCH colour (amber)
 *   STRONG SELL → AVOID colour (red)
 *
 * See /components/common/SignalBadge.jsx for the MAP definitions.
 */
const TvSignalBadge = ({ value }) => (
  <SignalBadge signal={value} size="sm" />
);

export default StocksPage;
